/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { PROFILE_TYPES } from '@seller-hermes/utils/profileTypes';
import { lazy, Suspense } from 'react';
import { store as Store } from '@seller-hermes/app/globals';
import { LoggedMobilePageLayout } from '@seller-hermes/app/logged-mobile-page-layout.component';
import { LoggedPageLayout } from '@seller-hermes/app/logged-page-layout.component';
import { UnloggedPageLayout } from '@seller-hermes/app/unlogged-page-layout.component';
import { getDriverRoute } from '@seller-hermes/features/route/routes.query';
import { getAccess as getRepassesAccess } from '@seller-hermes/features/repasses/api/client';
import {
  SellerGroupPermissions,
  SellerGroupUserPermissions,
} from '@seller-hermes/features/seller-group';
import {
  FeatureFlag,
  getSellerGroupFeatureFlag,
} from '@seller-hermes/hooks/useSellerGroupFeatureFlag';
import { userHasAllOfThesePermissions } from '@seller-hermes/hooks/useUserHasAnyOfThesePermissions/use-user-has-any-of-these-permissions.hook';
import {
  decideNoSellerRoute,
  fetchGroupHasActiveSellersFresh,
  fetchSellerIdsFresh,
  FIRST_SELLER_PATH,
  noSellerContextFromPortal,
  resolveFirstSellerPage,
} from '@seller-hermes/features/sellers/no-seller-guard';
import {
  leavePageTo,
  markLeavingPage,
} from '@seller-hermes/utils/chunk-error';
// ErrorPage fica eager: é o `errorElement` (renderizado quando algo quebra,
// inclusive falha ao carregar um chunk), então não pode depender de import
// dinâmico.
import ErrorPage from '@seller-hermes/pages/error/error.page';
import LoginContextPage from '@seller-hermes/pages/login/login-context.page';
import {
  bootstrapDashboardContext,
  DashboardContextRedirect,
} from '@seller-hermes/features/portal/context-bootstrap';
import {
  buildPortalHandoffUrl,
  getLocalPortalContextFromSearch,
} from '@seller-hermes/features/portal/portal-local-context';
// Os gates vêm do arquivo do hook, NUNCA do index da feature: o index
// reexporta as páginas, e importar dele puxava Workflows (xyflow, dagre) e
// Carrier Hub (ajv, modais) inteiros pra carga inicial, anulando o lazy().
import { canAccessWorkflows } from '@seller-hermes/features/workflows/hooks/useCanAccessWorkflows';
import {
  canAccessCarrierHub,
  canAccessCarrierHubMigration,
} from '@seller-hermes/features/carrier-hub/hooks/useCanAccessCarrierHub';
import { Auth } from '@aws-amplify/auth';
import { isValidRedirectUri } from '@seller-hermes/store/login/login.store';
import { hasCachedCognitoSession } from '@seller-hermes/utils/cognito-session-cache';
import {
  isLegacyDashboardRuntime,
  isStandaloneLoginRuntime,
} from '@seller-hermes/utils/runtime';
import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createBrowserRouter,
  LoaderFunctionArgs,
  Navigate,
  redirect,
} from 'react-router-dom';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
import { hasHiredModules, hasProfile } from './app-sidebar';

// Code-splitting: TODAS as páginas entram por demanda (lazy) — cada uma vira
// um chunk próprio, carregado só quando a rota é acessada. Os fallbacks de
// Suspense ficam nos layouts (LoggedPageLayout / UnloggedPageLayout /
// LoggedMobilePageLayout) e, pro TrpDisputePage (standalone), inline.
const ReportsPage = lazy(() =>
  import('@seller-hermes/pages/reports').then((m) => ({
    default: m.ReportsPage,
  })),
);
const DashboardPage = lazy(() => import('@seller-hermes/pages/dashboard'));
const AnalyticsPage = lazy(() =>
  import('@seller-hermes/pages/analytics').then((m) => ({
    default: m.AnalyticsPage,
  })),
);
const PaymentsPage = lazy(() =>
  import('@seller-hermes/pages/payments').then((m) => ({
    default: m.PaymentsPage,
  })),
);
const CareDisputesPage = lazy(
  () => import('@seller-hermes/features/care/pages/DisputesListPage'),
);
const WorkflowsListPage = lazy(
  () => import('@seller-hermes/features/workflows/WorkflowsListPage'),
);
const WorkflowEditorPage = lazy(
  () => import('@seller-hermes/features/workflows/WorkflowEditorPage'),
);
const WorkflowViewPage = lazy(
  () => import('@seller-hermes/features/workflows/WorkflowViewPage'),
);
const ContextSwitcherDevPage = lazy(() =>
  import('@seller-hermes/pages/dev/context-switcher-dev.page').then((m) => ({
    default: m.ContextSwitcherDevPage,
  })),
);
// Carrier Hub (gate real via is_legacy_integration, decisão 0.4)
const CarrierHubListPage = lazy(
  () => import('@seller-hermes/features/carrier-hub/pages/CarrierHubListPage'),
);
const CarrierHubDetailPage = lazy(
  () => import('@seller-hermes/features/carrier-hub/pages/CarrierHubDetailPage'),
);
const CarrierHubWizardPage = lazy(
  () => import('@seller-hermes/features/carrier-hub/pages/CarrierHubWizardPage'),
);
const CarrierHubMigratePage = lazy(
  () => import('@seller-hermes/features/carrier-hub/pages/CarrierHubMigratePage'),
);
const EngageCampaignsPage = lazy(
  () => import('@seller-hermes/features/engage/pages/CampaignsListPage'),
);
const EngageNewCampaignPage = lazy(
  () => import('@seller-hermes/features/engage/pages/NewCampaignPage'),
);
const EngageResultsPage = lazy(
  () => import('@seller-hermes/features/engage/pages/CampaignResultsPage'),
);
const EngageOptOutsPage = lazy(
  () => import('@seller-hermes/features/engage/pages/CampaignOptOutsPage'),
);
const EngageSmsPage = lazy(
  () => import('@seller-hermes/features/engage/pages/CampaignSmsPage'),
);
const RepassesEntregadoresPage = lazy(
  () => import('@seller-hermes/features/repasses/pages/EntregadoresPage'),
);
const RepassesTransferenciasPage = lazy(
  () => import('@seller-hermes/features/repasses/pages/TransferenciasPage'),
);
const RepassesSaldoPage = lazy(
  () => import('@seller-hermes/features/repasses/pages/SaldoPage'),
);
const RepassesConfiguracoesPage = lazy(
  () => import('@seller-hermes/features/repasses/pages/ConfiguracoesPage'),
);
const RepassesKycPage = lazy(
  () => import('@seller-hermes/features/repasses/pages/KycPage'),
);
const RepassesEntregadorDetalhePage = lazy(
  () => import('@seller-hermes/features/repasses/pages/EntregadorDetalhePage'),
);
const RepassesHomePage = lazy(
  () => import('@seller-hermes/features/repasses/pages/HomePage'),
);
const AuditoriaTransportadorasPage = lazy(
  () => import('@seller-hermes/features/auditoria/pages/TransportadorasPage'),
);
const AuditoriaFaturasPage = lazy(
  () => import('@seller-hermes/features/auditoria/pages/FaturasPage'),
);
const AuditoriaContratosPage = lazy(
  () => import('@seller-hermes/features/auditoria/pages/ContratosPage'),
);
const AuditoriaGlossarioPage = lazy(
  () => import('@seller-hermes/features/auditoria/pages/GlossarioPage'),
);
const AuditoriaFaturaDetalhePage = lazy(
  () => import('@seller-hermes/features/auditoria/pages/FaturaDetalhePage'),
);
const AuditoriaContratoDetalhePage = lazy(
  () => import('@seller-hermes/features/auditoria/pages/ContratoDetalhePage'),
);
const AuditoriaTransportadoraDetalhePage = lazy(
  () =>
    import('@seller-hermes/features/auditoria/pages/TransportadoraDetalhePage'),
);

// --- demais páginas (named exports) ---
const ApiTokensPage = lazy(() =>
  import('@seller-hermes/pages/api-tokens').then((m) => ({
    default: m.ApiTokensPage,
  })),
);
const CarrierIntegrationsPage = lazy(() =>
  import('@seller-hermes/pages/carrier-integrations').then((m) => ({
    default: m.CarrierIntegrationsPage,
  })),
);
const CatchUpPage = lazy(() =>
  import('@seller-hermes/pages/catch-up').then((m) => ({
    default: m.CatchUpPage,
  })),
);
const ChangelogPage = lazy(() =>
  import('@seller-hermes/pages/changelog').then((m) => ({
    default: m.ChangelogPage,
  })),
);
const ListCsatPageWithTheme = lazy(() =>
  import('@seller-hermes/pages/csat').then((m) => ({
    default: m.ListCsatPageWithTheme,
  })),
);
const DeliveriesPage = lazy(() =>
  import('@seller-hermes/pages/deliveries').then((m) => ({
    default: m.DeliveriesPage,
  })),
);
const DispatchRulesPage = lazy(() =>
  import('@seller-hermes/pages/dispatch-rules').then((m) => ({
    default: m.DispatchRulesPage,
  })),
);
const InactivityRulesPage = lazy(() =>
  import('@seller-hermes/pages/inactivity-rules').then((m) => ({
    default: m.InactivityRulesPage,
  })),
);
const MailLabelPage = lazy(() =>
  import('@seller-hermes/pages/mail-label').then((m) => ({
    default: m.MailLabelPage,
  })),
);
const NotificationsV2Page = lazy(() =>
  import('@seller-hermes/pages/notifications-v2').then((m) => ({
    default: m.NotificationsV2Page,
  })),
);
const NotificationsPage = lazy(() =>
  import('@seller-hermes/pages/notifications').then((m) => ({
    default: m.NotificationsPage,
  })),
);
const NotificationLogsPage = lazy(() =>
  import('@seller-hermes/pages/notification-logs').then((m) => ({
    default: m.NotificationLogsPage,
  })),
);
const OfferAutomationRulesPage = lazy(() =>
  import('@seller-hermes/pages/offer-automation-rules').then((m) => ({
    default: m.OfferAutomationRulesPage,
  })),
);
const OperationSettingsPage = lazy(() =>
  import('@seller-hermes/pages/operation').then((m) => ({
    default: m.OperationSettingsPage,
  })),
);
const StatusAliasesPage = lazy(() =>
  import('@seller-hermes/pages/status-aliases').then((m) => ({
    default: m.StatusAliasesPage,
  })),
);
const FiscalSettingsPage = lazy(() =>
  import('@seller-hermes/pages/fiscal').then((m) => ({
    default: m.FiscalSettingsPage,
  })),
);
const DelayRiskTiersPage = lazy(() =>
  import('@seller-hermes/pages/delay-risk-tiers').then((m) => ({
    default: m.DelayRiskTiersPage,
  })),
);
const PromisedDateRulesPage = lazy(() =>
  import('@seller-hermes/pages/promised-date-rules').then((m) => ({
    default: m.PromisedDateRulesPage,
  })),
);
const OrderIntegrationsPage = lazy(() =>
  import('@seller-hermes/pages/order-integrations').then((m) => ({
    default: m.OrderIntegrationsPage,
  })),
);
const PreferencesPage = lazy(() =>
  import('@seller-hermes/pages/preferences').then((m) => ({
    default: m.PreferencesPage,
  })),
);
const SecurityPage = lazy(() =>
  import('@seller-hermes/pages/security').then((m) => ({
    default: m.SecurityPage,
  })),
);
const QuotationRulesPage = lazy(() =>
  import('@seller-hermes/pages/quotation-rules').then((m) => ({
    default: m.QuotationRulesPage,
  })),
);
const ResendRulesPage = lazy(() =>
  import('@seller-hermes/pages/resend-rules').then((m) => ({
    default: m.ResendRulesPage,
  })),
);
const SellersPage = lazy(() =>
  import('@seller-hermes/pages/sellers').then((m) => ({
    default: m.SellersPage,
  })),
);
const ShippingTablesPage = lazy(() =>
  import('@seller-hermes/pages/shipping-tables').then((m) => ({
    default: m.ShippingTablesPage,
  })),
);
const ExternalTablesPage = lazy(() =>
  import('@seller-hermes/pages/external-tables').then((m) => ({
    default: m.ExternalTablesPage,
  })),
);
const SupportResponsiblesPage = lazy(() =>
  import('@seller-hermes/pages/support-responsibles').then((m) => ({
    default: m.SupportResponsiblesPage,
  })),
);
const TagsPage = lazy(() =>
  import('@seller-hermes/pages/tags/tags').then((m) => ({
    default: m.TagsPage,
  })),
);
const TagsRulesPage = lazy(() =>
  import('@seller-hermes/pages/tags-rules').then((m) => ({
    default: m.TagsRulesPage,
  })),
);
const ThemesPage = lazy(() =>
  import('@seller-hermes/pages/themes').then((m) => ({
    default: m.ThemesPage,
  })),
);
const TrackingPage = lazy(() =>
  import('@seller-hermes/pages/tracking').then((m) => ({
    default: m.TrackingPage,
  })),
);
const ViewOrderPage = lazy(() =>
  import('@seller-hermes/pages/view-order').then((m) => ({
    default: m.ViewOrderPage,
  })),
);
const WebhooksPage = lazy(() =>
  import('@seller-hermes/pages/webhooks').then((m) => ({
    default: m.WebhooksPage,
  })),
);
const LogsPage = lazy(() =>
  import('@seller-hermes/pages/logs').then((m) => ({ default: m.LogsPage })),
);
const WebhookLogDetailPage = lazy(() =>
  import('@seller-hermes/pages/webhook-log-detail').then((m) => ({
    default: m.WebhookLogDetailPage,
  })),
);

// --- demais páginas (default exports) ---
const TrpDisputePage = lazy(
  () => import('@seller-hermes/features/care-trp/TrpDisputePage'),
);
// Mock público (sem auth/layout) — seção web com o runbook do Agente de IA.
const RunbookMockPage = lazy(
  () => import('@seller-hermes/features/runbook-mock/RunbookMockPage'),
);
const CareConfigPage = lazy(
  () => import('@seller-hermes/features/care/pages/ConfigPage'),
);
const CareUsagePage = lazy(
  () => import('@seller-hermes/features/care/pages/UsagePage'),
);
const SupportTicketsPage = lazy(
  () =>
    import('@seller-hermes/features/support-tickets/pages/SupportTicketsPage'),
);
const DriversPage = lazy(() => import('@seller-hermes/pages/drivers'));
const DriverOnboardingPage = lazy(
  () => import('@seller-hermes/pages/driver-onboarding.page'),
);
const DriverTermsPage = lazy(
  () => import('@seller-hermes/pages/driver-terms.page'),
);
const ConvitePage = lazy(() => import('@seller-hermes/pages/convite.page'));
const DriverLocationPage = lazy(
  () => import('@seller-hermes/pages/driver-location/driver-location.page'),
);
const DriverCurrentRoutePage = lazy(
  () => import('@seller-hermes/pages/drivers/driver-current-route.page'),
);
const DriverRouteReviewPage = lazy(
  () => import('@seller-hermes/pages/drivers/driver-route-review.page'),
);
const DriverRouteSelectOrdersPage = lazy(
  () => import('@seller-hermes/pages/drivers/driver-route-select-orders'),
);
const ForgotPasswordPage = lazy(
  () => import('@seller-hermes/pages/login/forgot-password.page'),
);
const MainLoginPage = lazy(
  () => import('@seller-hermes/pages/login/main-login.page'),
);
const NewPasswordPage = lazy(
  () => import('@seller-hermes/pages/login/new-password.page'),
);
const MfaPage = lazy(() => import('@seller-hermes/pages/login/mfa.page'));
const ResetPasswordPage = lazy(
  () => import('@seller-hermes/pages/login/reset-password.page'),
);
const FirstSellerOnboardingPage = lazy(
  () => import('@seller-hermes/pages/onboarding/first-seller.page'),
);
// Todas as rotas de Pedidos entram pela `orders-entry`, que lê a flag de
// seller group `orders-page-v3` e escolhe entre `pages/orders.tsx` (v1) e
// `pages/orders-v3.tsx`. Ela própria só carrega o chunk escolhido — o outro
// nunca é baixado. Nome mantido (`OrdersV2Page`) pra não tocar nas ~40
// entradas de rota abaixo que já o referenciam.
const OrdersV2Page = lazy(() => import('@seller-hermes/pages/orders-entry'));
const RoutesPage = lazy(() => import('@seller-hermes/pages/routes'));
const CreateRoutePage = lazy(
  () => import('@seller-hermes/pages/routes/create-route.page'),
);
const ShippersPage = lazy(
  () => import('@seller-hermes/pages/shippers/shippers-list'),
);
const pagePermissions: Record<string, Array<keyof SellerGroupPermissions>> = {
  '/analytics': [],
  '/orders': ['read_order'],
  '/orders/create': ['read_order', 'create_order'],
  '/orders/quote-freight': ['read_order', 'create_order'],
  '/routes': ['read_route'],
  '/routes/create': ['create_route'],
  '/routes/create/select-orders': ['create_route'],
  '/deliveries': ['read_delivery'],
  '/deliveries/create': ['read_delivery', 'create_delivery'],
  '/driver/route/review': ['access_driver_route'],
  '/driver/route/current': ['access_driver_route'],
  '/drivers': ['read_driver'],
  '/drivers/onboarding': ['read_driver'],
  '/drivers/terms': ['read_driver'],
  '/dispatch-rules': ['read_dispatch_rules'],
  '/resend-rules': ['read_resend_rules'],
  '/inactivity-rules': ['read_inactivity_rules'],
  '/tags-rules': [],
  '/promised-date-rules': [],
  '/quotation-rules': ['read_dispatch_rules'],
  '/sellers': ['read_sellers'],
  '/csat': ['read_nps'],
  '/order-integrations': ['read_order_integrations'],
  '/carrier-integrations': ['read_carrier_integrations'],
  '/care/disputes': [],
  '/care/settings': [],
  '/care/usage': [],
  '/repasses/drivers': [],
  '/repasses/transfers': [],
  '/support/tickets': [],
  '/engage/campaigns': [],
  '/engage/campaigns/new': [],
  '/engage/campaigns/:id': [],
  '/engage/campaigns/:id/optouts': [],
  '/reports': ['read_report'],
  '/shippers': ['read_order', 'create_order', 'read_sellers'],
};

// Redirect que tira o usuário do app (portal, handoff de contexto).
//
// Pra outra origem NÃO devolve `redirect()` pro roteador: o React Router 6.4.0
// (@remix-run/router 1.0.0) não sabe sair do documento — chama
// history.push/replace com a URL absoluta. No push o navegador recusa e um
// catch (feito pro limite de pushState do iOS) cai em location.assign, então
// "funcionava"; no replace (trocar a aba de view, por exemplo) o replaceState
// lança SecurityError sem catch e o redirect simplesmente não acontecia — a
// pessoa sem sessão ficava presa na tela (Better Stack, 16/10).
//
// Então sai direto, marcando a saída ANTES (o navegador aborta os import() em
// andamento e, sem a marca, a recuperação de chunk recarregaria esta página).
// `replace`: voltar do portal não deve cair de novo aqui e ser rebatido. A
// promise nunca resolve: o loader fica "carregando" até o documento trocar,
// sem desenhar a tela com dado nenhum.
const redirectOut = (url: string): Response | Promise<never> => {
  let external = false;
  try {
    external =
      new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    // URL inválida: o redirect decide o que fazer com ela.
  }
  if (!external) return redirect(url);
  markLeavingPage();
  window.location.replace(url);
  return new Promise<never>(() => undefined);
};

export const getRouter = (store: typeof Store) => {
  const IfNotLoggedRedirectToLogin = async ({
    request: { url: rawUrl },
  }): Promise<void | Response> => {
    await store.persist.resolveRehydration();

    const portalUrl = process.env['NX_PORTAL_URL'];
    const isStandaloneRuntime = isStandaloneLoginRuntime();

    if (!hasCachedCognitoSession()) {
      store.getActions().login.setIsLogged(false);
      store.getActions().login.setIsLoading(false);
      store.getActions().login.setIsStarted(true);
      store.getActions().login.setUser(null);
      return isStandaloneRuntime || !portalUrl
        ? redirect('/login')
        : redirectOut(portalUrl);
    }

    try {
      await bootstrapDashboardContext();
    } catch (error) {
      if (error instanceof DashboardContextRedirect) {
        if (isStandaloneRuntime) {
          await store.getActions().start();
          if (store.getState().login.isLogged === false) {
            return redirect('/login');
          }
        }

        return redirectOut(error.url);
      }

      throw error;
    }

    await store.getActions().start();

    const url = new URL(rawUrl);
    const path = url.pathname;
    const thisPathPermission = pagePermissions[path];
    const user = store.getState().login.sellerGroupUser;

    // /me deu 403 de "sem filial ativa no grupo" (ADMIN/USER): não há
    // payload pra montar tela nenhuma, então tudo vai pro cadastro de filial,
    // que decide entre o formulário e o aviso pelo tipo do usuário no grupo
    // (vem do /portal/me). A própria rota de cadastro passa.
    if (store.getState().login.isLogged !== false && user.noActiveSellers) {
      return path === FIRST_SELLER_PATH ? undefined : redirect(FIRST_SELLER_PATH);
    }

    // Sem usuário também é "não logado": quando o /me falha (ex.: 403 de quem
    // não tem acesso a este produto) o start() dispara um logout() sem await,
    // então `isLogged` ainda está true aqui e o `user.user!` abaixo estourava
    // ("reading 'type'" no GO). Mesmo destino do logout.
    if (store.getState().login.isLogged === false || !user.user) {
      return isStandaloneRuntime || !portalUrl
        ? redirect('/login')
        : redirectOut(portalUrl);
    }

    // Domínio legado pós-virada: deixa de operar e vira porta de entrada do
    // portal, carregando o seller group em que o usuário estava.
    //
    // O redirect vive AQUI e não no app.tsx de propósito: em escopo de módulo
    // o store ainda não reidratou e o /me não rodou, então o seller group
    // seria desconhecido (ou viria do sessionStorage, que é por aba e pode
    // estar velho). Depois do start() acima o valor é autoritativo, vindo da
    // API. O custo é um /me antes de sair — uma vez por usuário, na transição.
    if (isLegacyDashboardRuntime() && portalUrl) {
      const sellerGroupId =
        store.getState().login.sellerGroupUser.user?.seller_group_id;

      return redirectOut(buildPortalHandoffUrl(portalUrl, sellerGroupId));
    }

    const localPortalContext = getLocalPortalContextFromSearch(url.search);
    if (localPortalContext) {
      // Local product and seller-group query params are a supported runtime
      // contract. Product is not validated against /me.profile_module because
      // ENTERPRISE/CARRIER profiles are a different dimension.
    }

    // Sem filial no grupo ativo: as telas mandariam `seller_ids: []` em toda
    // busca (500 em série em 19/10). Vai pra rota de cadastro, e o loader
    // dela decide entre formulário e aviso olhando se o GRUPO tem filial
    // (quem perdeu o vínculo num grupo com filial não pode ver o formulário).
    // Regra em no-seller-guard.ts.
    const noSeller = await decideNoSellerRoute({
      user: user.user,
      path,
      fetchSellerIds: fetchSellerIdsFresh,
    });
    if (noSeller === 'no-sellers') {
      return redirect(FIRST_SELLER_PATH);
    }

    const hasPermission = userHasAllOfThesePermissions(
      thisPathPermission || [],
      user.user!,
    );

    if (!hasPermission) {
      // find next page that user has permission
      const firstTruePermission = Object.entries(user.permissions)
        .sort((a, b) => {
          // sort read permissions first, then in the original db order
          if (a[0].startsWith('read') && !b[0].startsWith('read')) {
            return -1;
          }
          if (!a[0].startsWith('read') && b[0].startsWith('read')) {
            return 1;
          }
          return 0;
        })
        .filter(([_, value]) => value)[0];
      if (firstTruePermission) {
        // find first page that user has permission
        const firstTruePage = Object.entries(pagePermissions).find(
          ([_, value]) => {
            return value.includes(
              firstTruePermission[0] as keyof SellerGroupPermissions,
            );
          },
        );
        if (firstTruePage && firstTruePage[0]) {
          return redirect(firstTruePage[0]);
        }

        // deveriamos criar uma pagina 404 e jogar esse erro + ações comuns pro usuario
        // mas é uma situação extrema, o usuario só cai aqui se ele não tiver nenhuma permissão
        // de read ou access

        // const permissions = thisPathPermission;
        // const message = i18n.t('page-do-not-have-permissions', {
        //   permissions: permissions.join(', '),
        //   count: permissions.length,
        //   ns: 'permissions',
        // });
        // toast.error(message);
      }
      return;
    }
  };

  const IfLoggedRedirectToIndex = async ({ request }: LoaderFunctionArgs) => {
    await store.persist.resolveRehydration();

    if (!hasCachedCognitoSession()) {
      store.getActions().login.setIsLogged(false);
      store.getActions().login.setIsLoading(false);
      store.getActions().login.setIsStarted(true);
      store.getActions().login.setUser(null);

      const portalUrl = process.env['NX_PORTAL_URL'];
      return !isStandaloneLoginRuntime() && portalUrl
        ? redirectOut(portalUrl)
        : undefined;
    }

    await store.getActions().start();

    if (store.getState().login.isLogged === true) {
      const redirectUri = new URL(request.url).searchParams.get('redirect_uri');
      if (redirectUri && isValidRedirectUri(redirectUri)) {
        try {
          const session = await Auth.currentSession();
          const idToken = session.getIdToken().getJwtToken();
          const refreshToken = session.getRefreshToken().getToken();
          leavePageTo(`${redirectUri}#id_token=${idToken}&refresh_token=${refreshToken}`);
          return null;
        } catch {
          // session inválida, cai no redirect normal
        }
      }
      return redirect('/');
    }

    // Fora de um runtime standalone (LOCAL ou deploy preview com
    // NX_STANDALONE_LOGIN), a tela de login pertence ao portal. Manda o
    // usuário não autenticado pra lá em vez de mostrar o form interno.
    const portalUrl = process.env['NX_PORTAL_URL'];
    if (!isStandaloneLoginRuntime() && portalUrl) {
      return redirectOut(portalUrl);
    }

    return;
  };

  const IfStandaloneLoggedAllowContext = async () => {
    await store.persist.resolveRehydration();

    const portalUrl = process.env['NX_PORTAL_URL'];
    if (!isStandaloneLoginRuntime()) {
      return portalUrl ? redirectOut(portalUrl) : redirect('/');
    }

    if (!hasCachedCognitoSession()) {
      store.getActions().login.setIsLogged(false);
      store.getActions().login.setIsLoading(false);
      store.getActions().login.setIsStarted(true);
      store.getActions().login.setUser(null);
      return redirect('/login');
    }

    return;
  };

  // Care está liberado pra todas as marcas (com paywall freemium).
  // Captura de disputas é grátis; chat/resolução são bloqueados na UI
  // pra quem não tem `enable_care=true` (ver paywall no DisputeDrawer e
  // estado "chat_enabled=false" do care-service no tracking-page).
  // Pricing/Usage são abertas sem feature flag.
  const requireCareAccess = async (router: LoaderFunctionArgs) => {
    const res = await IfNotLoggedRedirectToLogin(router);
    if (res) return res;
    return;
  };

  // Pay (pay-into-hermes): auth-only no front. O gate "tem Pay?" é enforçado
  // no pay-api/BFF (feature flag `drivers` sobre pay.seller_groups). Ver
  // docs/pay-into-hermes.md §4.2.
  // SEGURANÇA: só brand_users (pay.brand_users) acessam Repasses/Auditoria —
  // enforçado no pay-api; aqui bloqueamos a rota também (fail-closed) pra quem
  // tenta o deep-link. `flag` gateia por feature flag do seller_group (espelha
  // os @RequireFlag): sem a flag, redireciona pra home (o menu já some).
  const requireRepassesAccess =
    (flag?: 'drivers' | 'carriers' | 'invoices' | 'contracts') =>
    async (router: LoaderFunctionArgs) => {
      const res = await IfNotLoggedRedirectToLogin(router);
      if (res) return res;
      try {
        const access = await getRepassesAccess();
        if (!access?.has_pay) return redirect('/');
        if (flag && access.flags?.[flag] !== true) return redirect('/');
      } catch {
        return redirect('/');
      }
      return;
    };

  // Configurações do Care são restritas a MASTER (INTERNAL bypassa tudo).
  // ADMIN/USER voltam pra lista de disputas.
  const requireCareConfigAccess = async (router: LoaderFunctionArgs) => {
    const res = await IfNotLoggedRedirectToLogin(router);
    if (res) return res;
    const { type } = store.getState().login.sellerGroupUser;
    if (type !== 'MASTER' && type !== 'INTERNAL') {
      return redirect('/care/disputes');
    }
    return;
  };

  const redirectIfNotOneOfTheseFeatureModulePermissions = async ({
    router,
    permissions,
    isInternal = false,
    featureFlag,
  }: {
    router: LoaderFunctionArgs;
    permissions: string[];
    isInternal?: boolean;
    featureFlag?: FeatureFlag;
  }) => {
    const res = await IfNotLoggedRedirectToLogin(router);
    if (res) return res;

    const user = store.getState().login.sellerGroupUser;

    if (featureFlag && !getSellerGroupFeatureFlag(featureFlag, user.user!)) {
      return redirect('/');
    }
    const somePermission = permissions.some(
      (p) =>
        user.user?.all_permissions?.[p] ||
        user.user?.hired_modules_permissions?.[p] ||
        user.user?.profile_permissions?.[p],
    );

    if (!somePermission) {
      return redirect('/');
    }
    if (isInternal && user.type !== 'INTERNAL') {
      return redirect('/');
    }
    return;
  };

  // Tela legada de integração de transportadora: some pro seller_group que
  // migrou ao Carrier Hub, inclusive por URL (F-04 da revisão 2026-09-24).
  // Vale pra INTERNAL também: em SG migrado quem edita é o Hub. SG legado
  // (flag true/ausente) segue exatamente como antes.
  const legacyCarrierIntegrationsLoader = async (router: LoaderFunctionArgs) => {
    const res = await redirectIfNotOneOfTheseFeatureModulePermissions({
      router,
      permissions: [SellerGroupUserPermissions.CARRIER_INTEGRATIONS],
    });
    if (res) return res;
    const user = store.getState().login.sellerGroupUser.user;
    if (user?.seller_group?.is_legacy_integration === false)
      return redirect('/carrier-hub');
    return null;
  };

  // Tabelas de frete antigas: em SG migrado vivem dentro do Carrier Hub
  // (aba Tabelas de Frete de cada transportadora). Mesmo gate de
  // legacyCarrierIntegrationsLoader.
  const legacyShippingTablesLoader = async (router: LoaderFunctionArgs) => {
    const res = await redirectIfNotOneOfTheseFeatureModulePermissions({
      router,
      permissions: [SellerGroupUserPermissions.FREIGHT_TABLES],
    });
    if (res) return res;
    const user = store.getState().login.sellerGroupUser.user;
    if (user?.seller_group?.is_legacy_integration === false)
      return redirect('/carrier-hub');
    return null;
  };

  return sentryCreateBrowserRouter([
    {
      // Care TRP-trail — rota pública, sem auth/layout. Auth é o próprio
      // token mágico na URL. Não tem `loader` nem `element`-wrapper, então
      // não passa por nenhum guard.
      path: '/trp/dispute/:token',
      element: (
        <Suspense fallback={null}>
          <TrpDisputePage />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: '/dev/context-switcher',
      element: (
        <Suspense fallback={null}>
          <ContextSwitcherDevPage />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    {
      element: <UnloggedPageLayout />,
      path: '/login/context',
      loader: IfStandaloneLoggedAllowContext,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          element: <LoginContextPage />,
        },
      ],
    },
    {
      // Wizard obrigatório de primeira filial — logado, mas fora do
      // LoggedPageLayout (sem sidebar): o usuário só segue depois de criar
      // a primeira filial do grupo (ou deslogar).
      path: FIRST_SELLER_PATH,
      element: (
        <Suspense fallback={null}>
          <FirstSellerOnboardingPage />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
      loader: async (router) => {
        // Preview dev-only do wizard (`?preview=1` em `pnpm dev`): pula o guard
        // de auth pra iterar no visual sem sessão Cognito. `import.meta.env.DEV`
        // é false no build de produção → este bloco vira dead code.
        if (
          import.meta.env.DEV &&
          new URL(router.request.url).searchParams.get('preview') === '1'
        ) {
          return null;
        }
        // Mesmo guard do painel (sessão, contexto do grupo ativo, /me). Pra
        // esta rota ele não aplica a regra de filial, senão faria loop.
        const res = await IfNotLoggedRedirectToLogin(router);
        if (res) return res;
        const { noActiveSellers, user: me } =
          store.getState().login.sellerGroupUser;
        const page = await resolveFirstSellerPage({
          noActiveSellers,
          me,
          // Memoizado no bootstrap: sem request novo.
          getPortalContext: async () =>
            noSellerContextFromPortal(
              await bootstrapDashboardContext().catch(() => null),
            ),
          fetchSellerIds: fetchSellerIdsFresh,
          groupHasActiveSellers: fetchGroupHasActiveSellersFresh,
        });
        return 'redirectTo' in page ? redirect(page.redirectTo) : page;
      },
    },
    {
      // Ponte do convite do motorista — rota PÚBLICA, sem auth/layout: quem
      // abre é o motorista, que não tem conta no Hermes. Redireciona pro
      // `appamo://`, porque o app não abre por link https. Ver a página.
      path: '/convite/:token',
      element: (
        <Suspense fallback={null}>
          <ConvitePage />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    {
      // Mock do Runbook do Agente de IA — rota pública, sem auth/layout.
      // Visível no deploy preview sem login (igual TrpDisputePage).
      path: '/mock/ai-runbook',
      element: (
        <Suspense fallback={null}>
          <RunbookMockPage />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
    },
    {
      element: <UnloggedPageLayout />,
      path: '/login',
      loader: IfLoggedRedirectToIndex,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          element: <MainLoginPage />,
        },
        {
          path: '/login/reset-password',
          element: <ResetPasswordPage />,
        },
        {
          path: '/login/forgot-password',
          element: <ForgotPasswordPage />,
        },
        {
          path: '/login/new-password',
          element: <NewPasswordPage />,
        },
        {
          path: '/login/mfa',
          element: <MfaPage />,
        },
      ],
    },
    {
      element: <LoggedPageLayout />,
      loader: IfNotLoggedRedirectToLogin,
      errorElement: <ErrorPage />,
      children: [
        {
          // Dashboard de acompanhamento (o "Dashboard Generativo") — o painel que
          // morava dentro da tela de Pedidos. LANÇAMENTO SOFT: só INTERNAL (staff
          // Abbiamo) por ora, enquanto a gente confere número por número contra o
          // antigo. Cliente (USER) e admin de cliente (MASTER) ainda não veem —
          // some do menu (espelhado no sidebar) E a URL direta cai na Home, senão
          // um link vazado abriria a tela pra quem não devia. Quando sair do soft,
          // troca aqui e no sidebar pra a permissão de pedido.
          path: '/dashboard',
          element: <DashboardPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            // Abre pra staff Abbiamo (INTERNAL) OU pra marca com a flag
            // `dashboard-generativo` no feature_config — mesmo par do
            // orders-page-v3. Sem um nem outro, cai na Home.
            const { type, user } = store.getState().login.sellerGroupUser;
            const liberado =
              type === 'INTERNAL' ||
              Boolean(getSellerGroupFeatureFlag('dashboard-generativo', user));
            if (!liberado) return redirect('/');
            return;
          },
        },
        {
          path: '/analytics',
          element: <AnalyticsPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            // Staff Abbiamo (INTERNAL) vê AS DUAS telas: bypassa o gate do
            // analytics pra o link "Dashboard (antigo)" nunca virar redirect.
            // Cliente segue no gate de sempre (permissão de dashboard + flag
            // `enable-analytics-page`) — inalterado.
            if (store.getState().login.sellerGroupUser.type === 'INTERNAL')
              return;
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.DASHBOARD],
              featureFlag: 'enable-analytics-page',
            });
          },
        },
        {
          path: '/routes/create',
          element: <CreateRoutePage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [
                SellerGroupUserPermissions.ROUTES_TO_CARRIER,
                SellerGroupUserPermissions.ROUTES_TO_PRIVATE_FLEET,
              ],
            });
          },
        },
        {
          path: '/drivers/onboarding',
          element: <DriverOnboardingPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.DRIVERS],
              featureFlag: 'driver_onboarding',
            });
          },
        },
        {
          path: '/drivers/terms',
          element: <DriverTermsPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.DRIVERS],
              featureFlag: 'driver_onboarding',
            });
          },
        },
        {
          path: '/drivers',
          element: <DriversPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.DRIVERS],
            });
          },
        },
        {
          path: '/dispatch-rules',
          element: <DispatchRulesPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [
                SellerGroupUserPermissions.DISPATCH_RULE_TO_CARRIER,
              ],
            });
          },
        },
        {
          path: '/offer-automation-rules',
          element: <OfferAutomationRulesPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.CROWDSHIPPING],
            });
          },
        },
        {
          path: '/resend-rules',
          element: <ResendRulesPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.RESEND_RULE_TO_CARRIER],
            });
          },
        },
        {
          path: '/tags-rules',
          element: <TagsRulesPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.TAGS_RULE],
            });
          },
        },
        // related quotations page will have paywall
        {
          path: '/quotations',
          element: <Navigate to="/" />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.QUOTATIONS],
            });
          },
        },
        {
          path: '/quotation-rules',
          element: <QuotationRulesPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.QUOTATION_RULES],
            });
          },
        },
        {
          path: '/sellers',
          element: <SellersPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.SELLERS],
            });
          },
        },
        {
          path: '/csat',
          element: <ListCsatPageWithTheme />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.CSAT],
            });
          },
        },
        {
          path: '/order-integrations',
          element: <OrderIntegrationsPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.ORDER_INTEGRATIONS],
            });
          },
        },
        {
          path: '/carrier-integrations',
          element: <CarrierIntegrationsPage />,
          loader: legacyCarrierIntegrationsLoader,
        },
        {
          path: '/carrier-integrations/radius',
          element: <CarrierIntegrationsPage />,
          loader: legacyCarrierIntegrationsLoader,
        },
        {
          path: '/carrier-integrations/cep',
          element: <CarrierIntegrationsPage />,
          loader: legacyCarrierIntegrationsLoader,
        },
        {
          path: '/reports',
          element: <ReportsPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.REPORTS],
            });
          },
        },
        {
          path: '/shipping-tables',
          element: <ShippingTablesPage />,
          loader: legacyShippingTablesLoader,
        },
        {
          path: '/shipping-tables/radius',
          element: <ShippingTablesPage />,
          loader: legacyShippingTablesLoader,
        },
        {
          path: '/shipping-tables/cep',
          element: <ShippingTablesPage />,
          loader: legacyShippingTablesLoader,
        },
        {
          path: '/external-tables',
          element: <ExternalTablesPage />,
          loader: (router) =>
            redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.FREIGHT_TABLES],
              featureFlag: 'external_shipping_table',
            }),
        },
        {
          path: '/shipping-tables/offers',
          element: <ShippingTablesPage />,
          loader: async (router) => {
            const res = await legacyShippingTablesLoader(router);
            if (res) return res;
            // Tabela de Ofertas é da precificação por rota do crowdshipping
            // (Abbiamo GO) — grupo LOG volta pras tabelas de frete normais.
            const sellerGroupUser = store.getState().login.sellerGroupUser;
            if (!hasHiredModules(sellerGroupUser?.user, ['GO'])) {
              return redirect('/shipping-tables');
            }
            // Gate por flag: só grupos com `crowdshipping_route_pricing` veem a
            // aba (rollout staged). Sem a flag, cai nas tabelas de frete.
            if (
              !getSellerGroupFeatureFlag(
                'crowdshipping_route_pricing',
                sellerGroupUser?.user,
              )
            ) {
              return redirect('/shipping-tables');
            }
            return;
          },
        },
        {
          path: '/inactivity-rules',
          element: <InactivityRulesPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [
                SellerGroupUserPermissions.INACTIVITY_RULE_TO_CARRIER,
              ],
            });
          },
        },
        {
          path: '/delay-risk-tiers',
          element: <DelayRiskTiersPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            // Leitura liberada pra MASTER/INTERNAL/ADMIN (USER de fora). O CRUD
            // no dashboard-api segue exigindo só MASTER/INTERNAL — os botões de
            // escrita na tela ficam desabilitados pra ADMIN pra não bater 401
            // (o 401 cai no interceptor global, que redireciona pro portal e
            // fecha um loop). Espelha o guard do item de menu.
            const userType = store.getState().login.sellerGroupUser.type;
            if (
              userType !== 'MASTER' &&
              userType !== 'INTERNAL' &&
              userType !== 'ADMIN'
            )
              return redirect('/');
            return;
          },
        },
        {
          path: '/promised-date-rules',
          element: <PromisedDateRulesPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            // Mesmo gate da automação de risco de atraso: a tela é
            // INTERNAL/MASTER (o CRUD no dashboard-api exige esses tipos e
            // responde 401 pros demais — e o 401 cai no interceptor global,
            // que redireciona pro portal e fecha um loop). Espelha o guard do
            // item de menu.
            const userType = store.getState().login.sellerGroupUser.type;
            if (userType !== 'MASTER' && userType !== 'INTERNAL')
              return redirect('/');
            return;
          },
        },
        {
          path: '/workflows',
          element: <WorkflowsListPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            if (!canAccessWorkflows(user)) return redirect('/');
            return;
          },
        },
        // Visualização (somente leitura) da versão no ar — ou do rascunho,
        // se nunca publicou. "Editar" leva pra /edit. Criação é modal na
        // lista, não há /new.
        {
          path: '/workflows/:id',
          element: <WorkflowViewPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            if (!canAccessWorkflows(user)) return redirect('/');
            return;
          },
        },
        {
          path: '/workflows/:id/edit',
          element: <WorkflowEditorPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            if (!canAccessWorkflows(user)) return redirect('/');
            return;
          },
        },
        // Uma versão publicada específica (vinda do histórico).
        {
          path: '/workflows/:id/versions/:versionId',
          element: <WorkflowViewPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            if (!canAccessWorkflows(user)) return redirect('/');
            return;
          },
        },
        // Carrier Hub — gate real (is_legacy_integration), decisão 0.4
        {
          path: '/carrier-hub',
          element: <CarrierHubListPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            if (!canAccessCarrierHub(user)) return redirect('/');
            return;
          },
        },
        {
          path: '/carrier-hub/migrate',
          element: <CarrierHubMigratePage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            // Acessível independente de is_legacy_integration — é o próprio
            // usuário do seller_group que completa a migração (decisão 0.4).
            if (!canAccessCarrierHubMigration(user)) return redirect('/');
            return;
          },
        },
        {
          path: '/carrier-hub/:id',
          element: <CarrierHubDetailPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            if (!canAccessCarrierHub(user)) return redirect('/');
            return;
          },
        },
        {
          path: '/carrier-hub/:id/wizard',
          element: <CarrierHubWizardPage />,
          loader: async (router) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;
            const user = store.getState().login.sellerGroupUser.user;
            // Só pós-virada (D5, 2026-09-15): a migração deriva as
            // configurações das integrações legadas sozinha, então o wizard
            // deixou de ser pré-requisito — filial que a derivação não
            // conseguiu montar (needs_config) é finalizada aqui DEPOIS do
            // complete, com o seller já no Hub.
            if (!canAccessCarrierHub(user)) return redirect('/');
            return;
          },
        },
        {
          path: '/payments',
          element: <PaymentsPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.PAYMENTS],
              featureFlag: 'enable_payments_page',
            });
          },
        },
        {
          path: '/deliveries',
          element: <DeliveriesPage />,
          loader: async (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.DELIVERIES],
            });
          },
        },
        {
          path: '/driver-location',
          element: <DriverLocationPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.DRIVERS],
            });
          },
        },
        {
          path: '/care/disputes',
          element: <CareDisputesPage />,
          loader: requireCareAccess,
        },
        {
          path: '/care/settings',
          element: <CareConfigPage />,
          loader: requireCareConfigAccess,
        },
        {
          // Path antigo — redireciona pra não quebrar links já espalhados.
          path: '/care/configuracao',
          loader: () => redirect('/care/settings'),
        },
        {
          path: '/care/usage',
          element: <CareUsagePage />,
          loader: requireCareAccess,
        },
        {
          path: '/repasses',
          element: <RepassesHomePage />,
          loader: requireRepassesAccess('drivers'),
        },
        {
          path: '/repasses/drivers',
          element: <RepassesEntregadoresPage />,
          loader: requireRepassesAccess('drivers'),
        },
        {
          path: '/repasses/drivers/:id',
          element: <RepassesEntregadorDetalhePage />,
          loader: requireRepassesAccess('drivers'),
        },
        {
          path: '/repasses/transfers',
          element: <RepassesTransferenciasPage />,
          loader: requireRepassesAccess('drivers'),
        },
        {
          path: '/repasses/balance',
          element: <RepassesSaldoPage />,
          loader: requireRepassesAccess('drivers'),
        },
        {
          // Verificação (KYC) da conta da marca. O gate "a marca tem
          // credencial Asaas?" é da TELA (e do item no menu), não daqui: o
          // loader só precisa garantir o mesmo acesso do resto do módulo.
          path: '/repasses/kyc',
          element: <RepassesKycPage />,
          loader: requireRepassesAccess('drivers'),
        },
        {
          path: '/repasses/settings',
          element: <RepassesConfiguracoesPage />,
          loader: requireRepassesAccess('drivers'),
        },
        {
          path: '/auditoria/transportadoras',
          element: <AuditoriaTransportadorasPage />,
          loader: requireRepassesAccess('carriers'),
        },
        {
          path: '/auditoria/faturas',
          element: <AuditoriaFaturasPage />,
          loader: requireRepassesAccess('invoices'),
        },
        {
          path: '/auditoria/contratos',
          element: <AuditoriaContratosPage />,
          loader: requireRepassesAccess('contracts'),
        },
        {
          path: '/auditoria/glossario',
          element: <AuditoriaGlossarioPage />,
          loader: requireRepassesAccess(),
        },
        {
          path: '/auditoria/faturas/:id',
          element: <AuditoriaFaturaDetalhePage />,
          loader: requireRepassesAccess('invoices'),
        },
        {
          path: '/auditoria/contratos/:id',
          element: <AuditoriaContratoDetalhePage />,
          loader: requireRepassesAccess('contracts'),
        },
        {
          path: '/auditoria/transportadoras/:id',
          element: <AuditoriaTransportadoraDetalhePage />,
          loader: requireRepassesAccess('carriers'),
        },
        {
          // Tickets marca ↔ Abbiamo (Linear). Liberado pra marca: o BFF escopa
          // por conta (a marca vê só os próprios chamados). É a entrada de
          // suporte — o "?" abre aqui e o modal antigo foi aposentado.
          path: '/support/tickets',
          element: <SupportTicketsPage />,
        },
        {
          // Engage (campanhas) — protótipo interno; visibilidade gateada
          // por INTERNAL na sidebar, loader só exige login por ora.
          path: '/engage/campaigns',
          element: <EngageCampaignsPage />,
          loader: requireCareAccess,
        },
        {
          path: '/engage/campaigns/new',
          element: <EngageNewCampaignPage />,
          loader: requireCareAccess,
        },
        {
          path: '/engage/campaigns/:id',
          element: <EngageResultsPage />,
          loader: requireCareAccess,
        },
        {
          path: '/engage/campaigns/:id/optouts',
          element: <EngageOptOutsPage />,
          loader: requireCareAccess,
        },
        {
          path: '/engage/campaigns/:id/sms',
          element: <EngageSmsPage />,
          loader: requireCareAccess,
        },
        {
          path: '/changelog',
          element: <ChangelogPage />,
        },
        {
          path: '/catch-up',
          element: <CatchUpPage />,
        },
        {
          path: '/support-responsibles',
          element: <SupportResponsiblesPage />,
        },
        {
          path: '/shippers',
          element: <ShippersPage />,
          loader: async (router: LoaderFunctionArgs) => {
            const res = await IfNotLoggedRedirectToLogin(router);
            if (res) return res;

            const sellerGroupUser = store.getState().login.sellerGroupUser;
            if (
              !hasProfile(sellerGroupUser?.user ?? null, PROFILE_TYPES.CARRIER) ||
              !hasHiredModules(sellerGroupUser.user, ['GO'])
            ) {
              return redirect('/');
            }
            return;
          },
        },
      ],
    },
    {
      element: <LoggedPageLayout />,
      errorElement: <ErrorPage />,
      loader: async (router) => {
        const res = await IfNotLoggedRedirectToLogin(router);
        if (res) return res;

        const path = new URL(router.request.url).pathname;

        if (path === '/v2/orders') {
          return redirect('/orders');
        }

        return;
      },
      children: [
        {
          path: '/',
          index: true,
          loader: async ({ request }) => {
            const search = new URL(request.url).search;
            // Preserve supported local product context on the default redirect.
            return redirect(`/orders${search}`);
          },
        },
        {
          path: '/orders/create',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/quote-freight',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/request-pickup',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/mark-collected',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/resend-order',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/upload-receipt',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/cancel-scheduled-delivery',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/duplicate-order',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/set-as-withdrawn',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/set-status',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/apply-order-tag',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/edit-shippers',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/:order_id/request-pickup',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/send-to-crowdshipping-delivery',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/resend-order',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/duplicate-order',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/upload-receipt',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/apply-order-tag',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/cancel-scheduled-delivery',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/cancel-crowdshipping-delivery',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/set-as-withdrawn',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id/set-status',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/:order_id',
          element: <ViewOrderPage />,
        },
        {
          path: '/orders/send-to-crowdshipping-delivery',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/cancel-crowdshipping-delivery',
          element: <OrdersV2Page />,
        },
        {
          path: '/orders/*',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/create',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/quote-freight',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/request-pickup',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/mark-collected',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/resend-order',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/upload-receipt',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/cancel-scheduled-delivery',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/cancel-crowdshipping-delivery',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/send-to-crowdshipping-delivery',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/duplicate-order',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/set-as-withdrawn',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/set-status',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/:order_id/request-pickup',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/send-to-crowdshipping-delivery',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/resend-order',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/duplicate-order',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/upload-receipt',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/apply-order-tag',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/cancel-scheduled-delivery',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/cancel-crowdshipping-delivery',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/set-as-withdrawn',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id/set-status',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/:order_id',
          element: <ViewOrderPage />,
        },
        {
          path: '/v2/orders/*',
          element: <OrdersV2Page />,
        },
        {
          path: '/v2/orders/apply-order-tag',
          element: <OrdersV2Page />,
        },
      ],
    },
    {
      element: <LoggedPageLayout />,
      errorElement: <ErrorPage />,
      loader: async (router) => {
        const res = await IfNotLoggedRedirectToLogin(router);
        if (res) return res;

        const path = new URL(router.request.url).pathname;

        if (path === '/v2/routes') {
          return redirect('/routes');
        }

        return;
      },
      children: [
        {
          path: '/',
          index: true,
          loader: async ({ request }) => {
            const search = new URL(request.url).search;
            // Preserve supported local product context on the default redirect.
            return redirect(`/routes${search}`);
          },
        },
        {
          path: '/routes/*',
          element: <RoutesPage />,
        },
        {
          path: '/v2/routes/*',
          element: <RoutesPage />,
        },
        {
          // Segurança da conta (2FA) — acessível a qualquer usuário logado,
          // por isso fica no grupo sem o bloqueio de type USER.
          path: '/settings/security',
          element: <SecurityPage />,
        },
      ],
    },
    {
      element: <LoggedPageLayout />,
      loader: async (router) => {
        const res = await IfNotLoggedRedirectToLogin(router);
        if (res) return res;

        const user = store.getState().login.sellerGroupUser;
        if (user.type === 'USER') {
          return redirect('/');
        }
        return;
      },
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/settings',
          element: <PreferencesPage />,
        },
        {
          path: '/settings/preferences',
          element: <PreferencesPage />,
        },
        {
          // Tela de usuários morreu aqui: dashboard e portal escreviam no
          // mesmo dado sem invalidar cache um do outro, gerando dessincronia
          // (filiais atribuídas via dash não refletiam no portal). Usuários
          // agora só são geridos em portal.abbiamolog.com/users — ver item da
          // sidebar em app-sidebar.tsx.
          path: '/settings/users',
          element: <Navigate to="/settings" />,
        },
        {
          path: '/settings/themes',
          element: <ThemesPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.THEMES],
            });
          },
        },
        {
          path: '/settings/webhooks',
          element: <WebhooksPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.WEBHOOKS],
            });
          },
        },
        {
          path: '/settings/logs',
          element: <Navigate to="/settings/logs/webhooks" />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.WEBHOOKS],
            });
          },
        },
        {
          path: '/settings/logs/webhooks',
          element: <LogsPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.WEBHOOKS],
            });
          },
        },
        {
          path: '/settings/logs/webhooks/:id',
          element: <WebhookLogDetailPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.WEBHOOKS],
            });
          },
        },
        {
          path: '/settings/notifications',
          element: <NotificationsPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.NOTIFICATIONS],
            });
          },
        },
        {
          // Hub novo de Notificações (rollout via feature flag
          // notifications_hub_v2 — o gate fino é na própria página, com
          // bypass em sandbox; aqui só a permissão de módulo).
          path: '/settings/notifications-v2',
          element: <NotificationsV2Page />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.NOTIFICATIONS],
            });
          },
        },
        {
          path: '/settings/notification-logs',
          element: <NotificationLogsPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.NOTIFICATIONS],
            });
          },
        },
        {
          path: '/settings/tokens',
          element: <ApiTokensPage />,
          loader: (router) => {
            return redirectIfNotOneOfTheseFeatureModulePermissions({
              router,
              permissions: [SellerGroupUserPermissions.API],
            });
          },
        },
        {
          path: '/settings/tags',
          element: <TagsPage />,
        },
        {
          path: '/settings/operation',
          element: <OperationSettingsPage />,
        },
        {
          path: '/settings/fiscal',
          element: <FiscalSettingsPage />,
        },
        {
          path: '/settings/status-aliases',
          element: <StatusAliasesPage />,
        },
        {
          path: '/settings/mail-label',
          element: <MailLabelPage />,
        },
        {
          path: '/settings/tracking',
          element: <TrackingPage />,
        },
      ],
    },
    {
      element: <LoggedMobilePageLayout />,
      loader: async (router) => {
        const res = await IfNotLoggedRedirectToLogin(router);
        if (res) return res;
        const route = await getDriverRoute().catch(() => null);
        const url = new URL(router.request.url);
        if (
          route &&
          ['/driver/route/select-orders', '/driver/route/review'].includes(
            url.pathname,
          )
        ) {
          return redirect('/driver/route/current');
        }

        if (!route && url.pathname === '/driver/route/current')
          return redirect('/driver/route/select-orders');

        return;
      },
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/driver/route/select-orders',
          element: <DriverRouteSelectOrdersPage />,
        },
        {
          path: '/driver/route/review',
          element: <DriverRouteReviewPage />,
        },
        {
          path: '/driver/route/current',
          element: <DriverCurrentRoutePage />,
        },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/" />,
    },
  ]);
};
